import { useCallback, useEffect, useMemo, useState } from 'react';
import backendClient from '../services/backendClient';

const LOCAL_STORAGE_FAVORITES_KEY = '9meals_favorite_meals';

export const getFavoriteMealId = (recipe = {}) => {
  const base = recipe.id || recipe.originalTitle || recipe.title || '';
  const mealType = recipe.mealType || '';
  return `${base}::${mealType}`.trim();
};

const normalizeFavoriteMeal = (recipe = {}) => ({
  ...recipe,
  favoriteId: getFavoriteMealId(recipe),
  id: recipe.id || null,
  originalTitle: recipe.originalTitle || null,
  title: recipe.title || '',
  description: recipe.description || '',
  mealType: recipe.mealType || '',
  imageURL: recipe.imageURL || '',
  instructions: recipe.instructions || null,
  instructionsFormatted: recipe.instructionsFormatted || '',
  ingredients: Array.isArray(recipe.ingredients) ? recipe.ingredients : [],
  maxStorageTime: recipe.maxStorageTime ?? recipe.freshness ?? null,
  cookingTime: recipe.cookingTime ?? recipe.cooking_time ?? '',
  savedAt: recipe.savedAt || new Date().toISOString(),
});

const readLocalFavorites = () => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_FAVORITES_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeLocalFavorites = (favoriteMeals) => {
  localStorage.setItem(LOCAL_STORAGE_FAVORITES_KEY, JSON.stringify(favoriteMeals));
};

const dedupeFavorites = (favoriteMeals) => {
  const map = new Map();
  favoriteMeals.forEach((meal) => {
    const normalizedMeal = normalizeFavoriteMeal(meal);
    if (!normalizedMeal.favoriteId) return;
    map.set(normalizedMeal.favoriteId, normalizedMeal);
  });
  return Array.from(map.values());
};

export const useFavoriteMeals = () => {
  const [favoriteMeals, setFavoriteMeals] = useState([]);
  const [isLoadingFavorites, setIsLoadingFavorites] = useState(true);

  const persistFavoriteMeals = useCallback(async (nextFavoriteMeals) => {
    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
      writeLocalFavorites(nextFavoriteMeals);
      return;
    }

    try {
      await backendClient.put('/api/user/favorites', { favoriteMeals: nextFavoriteMeals });
    } catch (error) {
      // Keep a local fallback so users do not lose favorites if backend call fails.
      writeLocalFavorites(nextFavoriteMeals);
    }
  }, []);

  const loadFavorites = useCallback(async () => {
    setIsLoadingFavorites(true);
    const authToken = localStorage.getItem('authToken');

    if (!authToken) {
      setFavoriteMeals(dedupeFavorites(readLocalFavorites()));
      setIsLoadingFavorites(false);
      return;
    }

    try {
      const response = await backendClient.get('/api/user/favorites');
      const remoteFavorites = Array.isArray(response?.favoriteMeals) ? response.favoriteMeals : [];
      const localFavorites = readLocalFavorites();
      const mergedFavorites = dedupeFavorites([...remoteFavorites, ...localFavorites]);

      setFavoriteMeals(mergedFavorites);

      const shouldSyncMergedToBackend = mergedFavorites.length !== remoteFavorites.length;
      if (shouldSyncMergedToBackend) {
        await backendClient.put('/api/user/favorites', { favoriteMeals: mergedFavorites });
      }

      if (localFavorites.length > 0) {
        localStorage.removeItem(LOCAL_STORAGE_FAVORITES_KEY);
      }
    } catch (error) {
      setFavoriteMeals(dedupeFavorites(readLocalFavorites()));
    } finally {
      setIsLoadingFavorites(false);
    }
  }, []);

  useEffect(() => {
    loadFavorites();

    const handleAuthChange = () => {
      loadFavorites();
    };

    window.addEventListener('authTokenChanged', handleAuthChange);
    return () => window.removeEventListener('authTokenChanged', handleAuthChange);
  }, [loadFavorites]);

  const isMealFavorite = useCallback((recipe) => {
    const favoriteId = getFavoriteMealId(recipe);
    if (!favoriteId) return false;
    return favoriteMeals.some((meal) => meal.favoriteId === favoriteId);
  }, [favoriteMeals]);

  const toggleFavoriteMeal = useCallback((recipe) => {
    const normalizedMeal = normalizeFavoriteMeal(recipe);
    if (!normalizedMeal.favoriteId) return;

    setFavoriteMeals((prev) => {
      const exists = prev.some((meal) => meal.favoriteId === normalizedMeal.favoriteId);
      const next = exists
        ? prev.filter((meal) => meal.favoriteId !== normalizedMeal.favoriteId)
        : dedupeFavorites([normalizedMeal, ...prev]);

      persistFavoriteMeals(next);
      return next;
    });
  }, [persistFavoriteMeals]);

  return {
    favoriteMeals,
    favoriteMealsCount: favoriteMeals.length,
    isLoadingFavorites,
    isMealFavorite,
    toggleFavoriteMeal,
    reloadFavoriteMeals: loadFavorites,
  };
};
